import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { ApiErrorBody } from './types';

export const isFetchBaseQueryError = (e: unknown): e is FetchBaseQueryError =>
  typeof e === 'object' && e !== null && 'status' in e;

const isApiErrorBody = (data: unknown): data is ApiErrorBody =>
  typeof data === 'object' && data !== null && 'message' in data;

/** Общая заглушка бэкенда: за ней всегда прячется настоящая причина. */
const GENERIC_MESSAGE = 'Ошибка запроса';

/**
 * Достаёт причину из тел, где message не заполнен или заполнен заглушкой:
 *   {"error": "Недостаточно лимитов для перезапуска поиска товара"}
 *   {"amount": ["Поле «Количество» должно быть больше 0"]}
 */
const detailedMessage = (data: unknown): string | null => {
  if (typeof data !== 'object' || data === null) return null;

  const body = data as Record<string, unknown>;

  if (typeof body.message === 'string' && body.message && body.message !== GENERIC_MESSAGE) {
    return null;
  }

  if (typeof body.error === 'string' && body.error) return body.error;

  for (const value of Object.values(body)) {
    if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) return value[0];
  }

  return null;
};

export const getErrorMessage = (error: unknown, fallback = 'Что-то пошло не так'): string => {
  if (isFetchBaseQueryError(error)) {
    const detailed = detailedMessage(error.data);
    if (detailed) return detailed;

    if (isApiErrorBody(error.data) && error.data.message) {
      return error.data.message;
    }
    if (error.status === 'FETCH_ERROR') return 'Нет связи с сервером';
    if (error.status === 'TIMEOUT_ERROR') return 'Сервер не ответил вовремя';
    if (error.status === 'PARSING_ERROR') return 'Сервер вернул некорректный ответ';
  }
  return fallback;
};

/** HTTP-код ошибки, если он есть. */
export const getErrorStatus = (error: unknown): number | undefined => {
  if (isFetchBaseQueryError(error) && typeof error.status === 'number') {
    return error.status;
  }
  return undefined;
};
